import { timestampDate, type Timestamp } from "@bufbuild/protobuf/wkt";
import { fromZonedTime } from "date-fns-tz";
import { LRUCache } from "lru-cache";

export type DateParts = { year?: number; month?: number; day?: number };
export type TimeOfDay = { hours: number; minutes: number; seconds: number; ms: number };

const DEFAULT_TIME_ZONE = "America/New_York";

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
	year: "numeric",
	month: "short",
	day: "numeric",
};

const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
	hour: "numeric",
	minute: "2-digit",
	second: "2-digit",
	hour12: true,
	timeZoneName: "short",
};

const dateFormatters: LRUCache<string, Intl.DateTimeFormat> = new LRUCache({ max: 10, ttlAutopurge: true });
const timeFormatters: LRUCache<string, Intl.DateTimeFormat> = new LRUCache({ max: 10, ttlAutopurge: true });

// Default params only cover `undefined`, but the backend can return "" for a user's timezone.
// Coalesce empty/whitespace values so we never pass "" to Intl (which throws RangeError on iOS).
function resolveTimeZone(timeZone: string): string {
	return timeZone.trim() === "" ? DEFAULT_TIME_ZONE : timeZone;
}

function getFormatterCacheKey(timeZone: string, options?: Intl.DateTimeFormatOptions): string {
	return JSON.stringify({
		timeZone,
		options,
	});
}

export function formatTimestamp(timestamp?: Timestamp, timeZone: string = DEFAULT_TIME_ZONE, dateOptions?: Intl.DateTimeFormatOptions, timeOptions?: Intl.DateTimeFormatOptions): string {
	if (!timestamp) {
		return "-";
	}
	const date = timestampDate(timestamp);
	return `${formatDate(date, timeZone, dateOptions)} at ${formatTime(date, timeZone, timeOptions)}`;
}

export function formatTimestampAsDate(timestamp?: Timestamp, timeZone: string = DEFAULT_TIME_ZONE): string {
	if (!timestamp) {
		return "-";
	}
	return formatDate(timestampDate(timestamp), timeZone);
}

export function formatDate(date?: Date, timeZone: string = DEFAULT_TIME_ZONE, options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS): string {
	if (!date) {
		return "-";
	}
	const tz = resolveTimeZone(timeZone);
	const cacheKey = getFormatterCacheKey(tz, options);
	let dateFormatter = dateFormatters.get(cacheKey);
	if (!dateFormatter) {
		try {
			dateFormatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone: tz });
		} catch {
			dateFormatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone: DEFAULT_TIME_ZONE });
		}
		dateFormatters.set(cacheKey, dateFormatter);
	}
	return dateFormatter.format(date);
}

export function formatTime(date?: Date, timeZone: string = DEFAULT_TIME_ZONE, options: Intl.DateTimeFormatOptions = DEFAULT_TIME_OPTIONS): string {
	if (!date) {
		return "-";
	}
	const tz = resolveTimeZone(timeZone);
	const cacheKey = getFormatterCacheKey(tz, options);
	let timeFormatter = timeFormatters.get(cacheKey);
	if (!timeFormatter) {
		try {
			timeFormatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone: tz });
		} catch {
			timeFormatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone: DEFAULT_TIME_ZONE });
		}
		timeFormatters.set(cacheKey, timeFormatter);
	}
	return timeFormatter.format(date);
}

export function formatRelativeTimestamp(timestamp: Timestamp, now: Date): string {
	const ms = timestampDate(timestamp).getTime();
	function ending(number: number): string {
		return number > 1 ? "s ago" : " ago";
	}
	let seconds = Math.floor((now.getTime() - ms) / 1000);
	const years = Math.floor(seconds / 31536000);
	if (years && years > 0) {
		return years + " year" + ending(years);
	}
	const months = Math.floor((seconds %= 31536000) / 2592000);
	if (months && months > 0) {
		return months + " month" + ending(months);
	}
	const weeks = Math.floor((seconds %= 2592000) / 604800);
	if (weeks && weeks > 0) {
		return weeks + " week" + ending(weeks);
	}
	const days = Math.floor((seconds %= 604800) / 86400);
	if (days && days > 0) {
		return days + " day" + ending(days);
	}
	const hours = Math.floor((seconds %= 86400) / 3600);
	if (hours && hours > 0) {
		return hours + " hour" + ending(hours);
	}
	const minutes = Math.floor((seconds % 3600) / 60);
	if (minutes && minutes > 0) {
		return minutes + " minute" + ending(minutes);
	}
	return "less than a minute ago";
}

export function formatRelativeDate(date: Date, timezone: string) {
	const now = new Date();
	const diff = now.getTime() - date.getTime();
	const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));
	if (diffDays === 0) {
		return "Today";
	} else if (diffDays === 1) {
		return "Yesterday";
	} else {
		return formatDate(date, timezone);
	}
}

export function formatRelativeTime(date: Date, timezone: string) {
	const now = new Date();
	const diff = now.getTime() - date.getTime();
	const diffHours = Math.floor(diff / (1000 * 60 * 60));
	const diffMinutes = Math.floor(diff / (1000 * 60));
	if (diffHours === 0) {
		return diffMinutes + " minutes ago";
	} else if (diffHours === 1) {
		return "1 hour ago";
	} else if (diffHours < 12) {
		return diffHours + " hours ago";
	} else {
		return formatTime(date, timezone);
	}
}

export function convertDatePartsToTimestamp(date?: DateParts, hour?: number | null): number | undefined {
	if (!date?.year || !date.month || !date.day) {
		return undefined;
	}
	const current = new Date(date.year, date.month - 1, date.day);
	if (typeof hour === "number") {
		current.setHours(hour);
	}
	return current.getTime();
}

export function zonedDateTime(date: Date, timeZone: string = DEFAULT_TIME_ZONE, timeOfDay: TimeOfDay = { hours: 0, minutes: 0, seconds: 0, ms: 0 }): Date {
	return fromZonedTime(new Date(date.getFullYear(), date.getMonth(), date.getDate(), timeOfDay.hours, timeOfDay.minutes, timeOfDay.seconds, timeOfDay.ms), resolveTimeZone(timeZone));
}
